import { z } from 'zod';
import { optionalText, phoneSchema, text } from './common.js';
import { nonNegativeMoneySchema } from '../money.js';
import { COMPLIANCE_PAGE_KINDS } from '../enums.js';
/**
 * Portfolio-wide settings the ADMIN can edit at runtime — see
 * CompanySettings' doc comment in schema.prisma for why this is a
 * deliberate, narrow departure from the app's usual env-var-only
 * white-labeling stance.
 */
export const updateCompanySettingsSchema = z.object({
    // Nullish, not required: clearing the field back to "not configured" is a
    // legitimate action (the contact link then simply doesn't render), not an
    // error state.
    contactEmail: z.string().trim().toLowerCase().email().max(320).nullish(),
});
/**
 * Legal/contractual company facts used to autofill generated documents —
 * see CompanyDocumentProfile's doc comment in schema.prisma for why this is
 * a separate table (and separate schema) from CompanySettings. Every field
 * is nullish: clearing one back to "not configured" is legitimate, same
 * posture as `updateCompanySettingsSchema.contactEmail`.
 */
export const updateCompanyDocumentProfileSchema = z.object({
    legalName: optionalText(200),
    mailingLine1: optionalText(200),
    mailingLine2: optionalText(200),
    mailingCity: optionalText(100),
    mailingState: z.literal('MI').nullish(),
    mailingPostalCode: z
        .string()
        .trim()
        .regex(/^\d{5}(-\d{4})?$/, 'Enter a valid ZIP code.')
        .nullish(),
    phone: phoneSchema.nullish(),
    licenseNumber: optionalText(100),
    signatoryName: optionalText(200),
    signatoryTitle: optionalText(200),
    // Basis points out of 10000 (Ownership.basisPoints' own convention), not a
    // percent — the form converts. Nullish: clearing it back to "no fee" is
    // legitimate, same posture as every other field here.
    managementFeeBasisPoints: z.number().int().min(0).max(10000).nullish(),
    // Plain firm-authored text, not a merge-field document — see the matching
    // Prisma field's doc comment in schema.prisma. Nullish: clearing it back
    // to "not configured" is legitimate, same posture as every field above.
    smsConsentDisclosureText: optionalText(1000),
});
/**
 * Firm-wide tenant fee schedule — see FeeSchedule's doc comment in
 * schema.prisma. Unlike CompanyDocumentProfile's nullish fields, every field
 * here is required: there is no "not configured" state, every lease is
 * always subject to some amount, defaulting to $50.00 at the DB level.
 */
export const updateFeeScheduleSchema = z.object({
    lateFee5DayAmount: nonNegativeMoneySchema,
    lateFee10DayAmount: nonNegativeMoneySchema,
    habitualLateFeeAmount: nonNegativeMoneySchema,
    nsfFeeAmount: nonNegativeMoneySchema,
});
export const compliancePageKindSchema = z.enum(COMPLIANCE_PAGE_KINDS);
/**
 * Body content for one of the four public compliance/policy pages —
 * sanitized rich-text HTML from the shared RichTextEditor, same allowlist
 * and length ceiling as DocumentTemplate.body
 * (createDocumentTemplateSchema).
 */
export const updateCompliancePageSchema = z.object({
    body: text(50000),
});
//# sourceMappingURL=settings.js.map